import React, { useEffect, useState } from 'react';
import { stalkerAPI } from '../../services/api';
import { useDarkMode } from '../../contexts/DarkModeContext';

interface StalkerSetting {
	setting_key: string;
	setting_value: string | null;
}

const FIELD_LABELS: Record<string, string> = {
	username: 'Username (VSCO / Instagram / TikTok)',
	profilePicUrl: 'Profile Picture (Instagram)',
	avatarUrl: 'Profile Picture (VSCO / TikTok)',
	mediaCount: 'Media Count (Instagram)',
	videoCount: 'Media Count (TikTok)',
	isPrivate: 'Profile Privacy',
	followerCount: 'Follower Count',
	followingCount: 'Following Count',
	isVerified: 'Verification Status',
	externalUrl: 'External URL',
	name: 'Profile Name',
	bio: 'Profile Bio',
	language: 'Language',
	nicknameModifyTime: 'Nickname Modify Time',
	uniqueIdModifyTime: 'Unique ID Modify Time',
	tiktokId: 'TikTok ID',
	secUid: 'TikTok SecUID',
	profileLink: 'Profile Link',
};

const StalkerSettings: React.FC = () => {
	const { isDark } = useDarkMode();
	const [settings, setSettings] = useState<StalkerSetting[]>([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		const loadSettings = async () => {
			try {
				const resp = await stalkerAPI.settings.getAll();
				setSettings(resp.data.data || []);
			} catch (error) {
				console.error('Error loading stalker settings:', error);
			} finally {
				setLoading(false);
			}
		};

		loadSettings();
	}, []);

	const getValue = (key: string): string | null => {
		const item = settings.find((s) => s.setting_key === key);
		return item ? item.setting_value : null;
	};

	const updateLocalSetting = (key: string, value: string | null) => {
		setSettings((prev) => {
			const existingIndex = prev.findIndex((s) => s.setting_key === key);
			if (existingIndex === -1) {
				return [...prev, { setting_key: key, setting_value: value }];
			}
			const clone = [...prev];
			clone[existingIndex] = { ...clone[existingIndex], setting_value: value };
			return clone;
		});
	};

	const handleToggle = (field: string, checked: boolean) => {
		updateLocalSetting(`telegram_notification_${field}`, checked ? 'true' : 'false');
	};

	const handleSave = async () => {
		try {
			setSaving(true);
			const payload: Record<string, string | null> = {};
			settings.forEach((s) => {
				payload[s.setting_key] = s.setting_value;
			});
			const resp = await stalkerAPI.settings.update(payload);
			setSettings(resp.data.data || []);
		} catch (error) {
			console.error('Error saving stalker settings:', error);
		} finally {
			setSaving(false);
		}
	};

	if(loading){
		return (
			<div className={`flex flex-col items-center justify-center h-64 font-mono text-xs ${isDark ? 'text-emerald-500' : 'text-emerald-600'}`}>
				<div className="flex items-center space-x-2">
					<div className="w-1 h-3 bg-current animate-pulse" />
					<span>[ FETCHING_STALKER_CONFIG... ]</span>
				</div>
			</div>
		);
	}

	const messageToUser = getValue('telegram_notification_MESSAGE_TO_USER') || '';

	return (
		<div className="space-y-6 px-2 md:px-0">
			{/* Header */}
			<div className={`flex flex-col md:flex-row items-start justify-between md:items-center p-4 border rounded-none ${isDark ? 'bg-[#111] border-[#222]' : 'bg-white border-neutral-200'} gap-4`}>
				<div>
					<h1 className={`text-sm font-mono font-bold tracking-widest uppercase ${isDark ? 'text-neutral-200' : 'text-neutral-800'}`}>
						// SYS: STALKER_SETTINGS
					</h1>
					<p className={`text-[10px] font-mono mt-0.5 uppercase tracking-wider ${isDark ? 'text-emerald-500/70' : 'text-emerald-600/70'}`}>
						MANAGE_NOTIFICATION_PREFERENCES
					</p>
				</div>
			</div>

			<div className={`p-4 md:p-6 border rounded-none ${isDark ? 'bg-[#111] border-[#222]' : 'bg-white border-neutral-200'}`}>
				<h2 className={`text-[11px] font-mono font-bold tracking-widest uppercase mb-4 pb-2 border-b ${isDark ? 'text-emerald-500 border-[#333]' : 'text-emerald-600 border-neutral-200'}`}>
					TELEGRAM_NOTIFICATIONS
				</h2>

				<div className="space-y-6">
					<div>
						<label className={`block text-[10px] font-mono tracking-widest uppercase mb-2 ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
							TARGET_CHAT_ID {"{"}telegram_notification_MESSAGE_TO_USER{"}"}
						</label>
						<input
							type="text"
							value={messageToUser}
							onChange={(e) =>
								updateLocalSetting('telegram_notification_MESSAGE_TO_USER', e.target.value || null)
							}
							className={`w-full px-4 py-2 text-xs font-mono border outline-none transition-colors ${
								isDark
									? 'bg-[#0a0a0a] border-[#333] text-neutral-200 focus:border-emerald-500/50'
									: 'bg-neutral-50 border-neutral-300 text-neutral-800 focus:border-emerald-500/50'
							}`}
							placeholder="ENTER_TELEGRAM_USER_ID_OR_CHAT_ID"
						/>
						<p className={`mt-2 text-[9px] font-mono uppercase tracking-wider ${isDark ? 'text-neutral-500' : 'text-neutral-500'}`}>
							// THIS USER WILL RECEIVE TELEGRAM NOTIFICATIONS FOR ENABLED CHANGES.
						</p>
					</div>

					<div className={`pt-4 border-t ${isDark ? 'border-[#333]' : 'border-neutral-200'}`}>
						<h3 className={`text-[10px] font-mono font-bold tracking-widest uppercase mb-3 ${isDark ? 'text-neutral-300' : 'text-neutral-800'}`}>
							TRACKED_FIELDS
						</h3>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-2">
							{Object.entries(FIELD_LABELS).map(([field, label]) => {
								const key = `telegram_notification_${field}`;
								const raw = getValue(key);
								const enabled = (raw || '').toLowerCase() === 'true' || raw === '1';
								return (
									<label
										key={field}
										className={`flex items-center justify-between p-3 border transition-colors cursor-pointer ${
											isDark ? (enabled ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-[#0a0a0a] border-[#333] hover:border-[#444]') : (enabled ? 'bg-emerald-50 border-emerald-200' : 'bg-neutral-50 border-neutral-200 hover:border-neutral-300')
										}`}
									>
										<span className={`text-[10px] font-mono tracking-wider uppercase ${isDark ? (enabled ? 'text-emerald-400' : 'text-neutral-400') : (enabled ? 'text-emerald-700' : 'text-neutral-600')}`}>
											{label.replace(/\s+/g, '_') /* keep visually techy */}
										</span>
										<input
											type="checkbox"
											checked={enabled}
											onChange={(e) => handleToggle(field, e.target.checked)}
											className="w-3.5 h-3.5 accent-emerald-500 bg-[#0a0a0a] border-[#333] cursor-pointer"
										/>
									</label>
								);
							})}
						</div>
					</div>

					<div className={`pt-4 flex border-t ${isDark ? 'border-[#333]' : 'border-neutral-200'}`}>
						<button
							type="button"
							onClick={handleSave}
							disabled={saving}
							className={`px-6 py-2 text-[10px] font-mono font-bold tracking-widest uppercase border transition-colors ml-auto flex items-center space-x-2 ${
								saving 
									? (isDark ? 'bg-emerald-500/20 text-emerald-500/50 border-emerald-500/20 cursor-not-allowed' : 'bg-emerald-100 text-emerald-600/50 border-emerald-200 cursor-not-allowed')
									: (isDark ? 'bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200')
							}`}
						>
							{saving ? (
								<>
									<div className="w-1 h-2 bg-current animate-pulse mr-2" />
									SAVING_CONFIG...
								</>
							) : (
								<>APPLY_CHANGES</>
							)}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default StalkerSettings;

